/**
 * feesApi.js
 * ----------
 * Real API client for Admission Fees submodule.
 * Connects directly to backend FastAPI endpoints for fee records, KPI metrics,
 * student fee detail breakdown, and overdue SMS reminders.
 */
import api from '../../../../api/client'

/**
 * Build option arrays for fee filter dropdowns from real master lookups.
 */
function toOptions(values) {
  return [
    { label: 'All', value: 'All' },
    ...(Array.isArray(values) ? values.filter(Boolean) : []).map((v) => ({ label: v, value: v })),
  ]
}

export let COURSE_OPTIONS = [{ label: 'All', value: 'All' }]
export let BATCH_OPTIONS = [{ label: 'All', value: 'All' }]
export const STATUS_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Paid', value: 'Paid' },
  { label: 'Balance', value: 'Balance' },
  { label: 'OverDue', value: 'OverDue' },
  { label: 'Partial', value: 'Partial' },
]

async function fetchOptionList(lookup) {
  try {
    const { data } = await api.get(`/admission/masters/${lookup}`)
    const values = Array.isArray(data) ? data : (Array.isArray(data?.options) ? data.options : [])
    return values
  } catch {
    return []
  }
}

export async function loadFeeOptions() {
  const [courses, batches] = await Promise.all([
    fetchOptionList('courses'),
    fetchOptionList('batches'),
  ])
  COURSE_OPTIONS = toOptions(courses)
  BATCH_OPTIONS = toOptions(batches)
}

loadFeeOptions()

/**
 * Get fees dashboard summary cards: total, paid, balance, overdue.
 */
export const getFeesSummary = async (params = {}) => {
  const { data } = await api.get('/admission/fees/summary', {
    params: {
      studentId: params.studentId || undefined,
      companyFilter: params.companyFilter || undefined,
      branchFilter: params.branchFilter || undefined,
      courseFilter: params.courseFilter || undefined,
      batchFilter: params.batchFilter || undefined,
      schemeFilter: params.schemeFilter || undefined,
      typeFilter: params.typeFilter || undefined,
      genderFilter: params.genderFilter || undefined,
      statusFilter: params.statusFilter || undefined,
      fromDate: params.fromDate || undefined,
      toDate: params.toDate || undefined,
      tab: params.tab || 'active',
    },
  })
  return {
    total: Number(data?.total || 0),
    paid: Number(data?.paid || 0),
    balance: Number(data?.balance || 0),
    overdue: Number(data?.overdue || 0),
  }
}

/**
 * Get filtered fees rows.
 */
export const listFees = async (params = {}) => {
  const query = {
    searchField: params.searchField || 'Name',
    searchText: params.searchText || '',
    companyFilter: params.companyFilter || 'All',
    branchFilter: params.branchFilter || 'All',
    courseFilter: params.courseFilter || 'All',
    schemeFilter: params.schemeFilter || 'All',
    typeFilter: params.typeFilter || 'All',
    batchFilter: params.batchFilter || 'All',
    academicYearFilter: params.academicYearFilter || 'All',
    subjectFilter: params.subjectFilter || 'All',
    genderFilter: params.genderFilter || 'All',
    statusFilter: params.statusFilter || 'All',
    fromDate: params.fromDate || undefined,
    toDate: params.toDate || undefined,
    studentId: params.studentId || undefined,
  }
  const { data } = await api.get('/admission/fees', { params: query })
  return Array.isArray(data) ? data : []
}

/**
 * Get detailed student fee information including installment schedule.
 */
export const getStudentFeeDetails = async (studentId) => {
  const { data } = await api.get(`/admission/fees/${studentId}`)
  return data
}

/**
 * Dispatch overdue reminder SMS messages and audit log into StudentMessages.
 */
export const sendOverdueSms = async ({ studentIds, customMessage }) => {
  const { data } = await api.post('/admission/fees/send-sms', {
    studentIds,
    customMessage,
  })
  return data
}

/**
 * Build default overdue SMS message text matching legacy ERP specification.
 */
export function formatOverdueSmsMessage(student) {
  const guardian = student.guardianName || `Parent of ${student.firstName || student.studentName || 'Student'}`
  const sonOrWard = student.gender === 'Female' ? 'daughter' : 'son'
  const studentName = student.firstName || (student.studentName ? student.studentName.split(' ')[0] : 'Student')
  const overdueAmt = Math.round(Number(student.overdue || student.balance || 0))

  const institute = student.institute || student.company || ''
  const suffix = institute ? ` ${institute}` : ''
  return `Dear ${guardian}, Rs.${overdueAmt} is overdue towards fees of your ${sonOrWard} ${studentName}. Kindly pay at the earliest. Please ignore if already paid. Thank you.${suffix}`
}
